import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Tabs,
  Tag,
  Tooltip,
  message,
  Popconfirm,
  Space,
  Row,
  Col,
  Typography,
  Divider,
  Collapse,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  PoweroffOutlined,
  ApiOutlined,
  DownloadOutlined,
  MinusCircleOutlined,
  ApartmentOutlined,
  SettingOutlined,
  FilterOutlined,
  TableOutlined,
} from "@ant-design/icons";
import Heading from "../../../components/Heading";
import { ReportConfigService } from "../../../services/api/reportConfig";
import "./index.scss";

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { Text } = Typography;
const { Panel } = Collapse;

// ── Helpers ─────────────────────────────────────────────────────────────────

function extractCols(sql = "") {
  const clean = sql.replace(/\s+/g, " ").trim();
  const m = clean.match(/(?:^|\s)SELECT\s+(.+?)\s+FROM\s/i);
  if (!m) return [];
  return splitParens(m[1])
    .map((p) => {
      p = p.trim();
      const alias = p.match(/\bAS\s+(\w+)\s*$/i);
      if (alias) return alias[1].toLowerCase();
      const tokens = p.split(/[\s.]+/);
      return tokens[tokens.length - 1].toLowerCase().replace(/[^a-z0-9_]/g, "");
    })
    .filter(Boolean);
}

function splitParens(s) {
  const r = [];
  let d = 0;
  let cur = "";
  for (const c of s) {
    if (c === "(") d++;
    else if (c === ")") d--;
    else if (c === "," && d === 0) { r.push(cur); cur = ""; continue; }
    cur += c;
  }
  if (cur) r.push(cur);
  return r;
}

function toTitleCase(snake) {
  return snake.split("_").map((w) => (w ? w[0].toUpperCase() + w.slice(1) : "")).join(" ");
}

function copyToClipboard(url) {
  navigator.clipboard
    .writeText(window.location.origin + url)
    .then(() => message.success("URL copied to clipboard"))
    .catch(() => message.info(url));
}

// ── SQL Preview sub-component ────────────────────────────────────────────────

function SqlPreview({ sql }) {
  const cols = extractCols(sql);
  const rows = [
    { label: "S.No", key: "_sno" },
    ...cols.map(c => ({ label: toTitleCase(c), key: c }))
  ];
  const isEmpty = !cols.length;

  if (isEmpty) {
    return (
      <div className="rcm-sql-preview-empty">
        <span role="img" aria-label="doc">📋</span> Column Mapping Preview <small>(auto from SQL)</small>
        <div style={{ color: "#8c8c8c", marginTop: 8, fontSize: 13 }}>Enter a valid SELECT query...</div>
      </div>
    );
  }

  return (
    <div className="rcm-simple-preview-container">
      <div className="rcm-preview-label">
        <span role="img" aria-label="doc">📋</span> Column Mapping Preview <small>(auto from SQL)</small>
      </div>
      <div className="rcm-simple-table-wrapper">
        <table className="rcm-simple-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Header Label (Title Case)</th>
              <th>Data Key (JSON API)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key}>
                <td>{i + 1}</td>
                <td>{r.label}</td>
                <td>{r.key}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Filter type options ───────────────────────────────────────────────────────

const FILTER_TYPES = [
  { value: "exact",        label: "exact — equals match" },
  { value: "text",         label: "text — ILIKE search" },
  { value: "date_from",    label: "date_from — from date (>=)" },
  { value: "date_to",      label: "date_to — to date (<=)" },
  { value: "date_range",   label: "date_range — from + to" },
  { value: "select",       label: "select — single dropdown" },
  { value: "multi_select", label: "multi_select — multi dropdown" },
  { value: "dynamic_select",       label: "dynamic_select — API-loaded dropdown (single)" },
  { value: "dynamic_multi_select_string", label: "dynamic_multi_select_string — API-loaded dropdown (multi-string)" },
  { value: "dynamic_multi_select_int", label: "dynamic_multi_select_int — API-loaded dropdown (multi-integer)" },
  { value: "list_integer", label: "list_integer — IN(…) integers" },
  { value: "list_string",  label: "list_string — IN(…) strings" },
  { value: "integer",      label: "integer — numeric equality" },
  { value: "number",       label: "number — numeric range" },
];

// ── Main Component ───────────────────────────────────────────────────────────

const ReportConfigManager = () => {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, split: 0 });

  // ── Data Loading ────────────────────────────────────────────────────────

  const fetchData = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const result = await ReportConfigService.getAll(page - 1, pageSize);
      setTableData(result.data);
      const pag = result.pagination;
      setPagination((prev) => ({
        ...prev,
        current: (pag.currentPage ?? page - 1) + 1,
        pageSize: pag.pageSize ?? pageSize,
        total: pag.totalRecords ?? pag.totalElements ?? result.data.length,
      }));
      const active = result.data.filter((r) => r.isActive).length;
      const split = result.data.filter((r) => r.downloadSql).length;
      setStats({
        total: pag.totalRecords ?? pag.totalElements ?? result.data.length,
        active,
        inactive: result.data.length - active,
        split,
      });
    } catch (err) {
      message.error("Failed to load report configs: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(1, 10);
  }, [fetchData]);

  const handleTableChange = (pag) => {
    fetchData(pag.current, pag.pageSize);
  };

  // ── Modal ───────────────────────────────────────────────────────────────

  const openAdd = () => {
    setEditKey(null);
    form.resetFields();
    form.setFieldsValue({
      datasource: "REPLICATION",
      batchSize: 50000,
      isActive: true,
      filters: [],
      pageConfig: {
        defaultPageSize: 20,
        pageSizeOptions: "10,20,50,100",
        downloadable: true,
        requiresFilter: false,
        filtersCollapsed: false,
        showSerialNumber: true,
      },
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditKey(record.reportKey);
    form.resetFields();

    // Map columnUiConfig → form columns
    const columns = Array.isArray(record.columnUiConfig)
      ? record.columnUiConfig.map((c) => ({
        key: c.key || "",
        header: c.header || "",
        cellType: c.cellType || "text",
        sortable: c.sortable ?? true,
        hidden: c.hidden || false,
        width: c.width || "",
        badgeColorsRaw: c.badgeColors
          ? Object.entries(c.badgeColors).map(([k, v]) => `${k}=${v}`).join("\n")
          : "",
        linkTemplate: c.linkTemplate || "",
        linkTarget: c.linkTarget || "_blank",
        clickReportKey: c.clickReportKey || "",
        clickParamMapRaw: c.clickParamMap
          ? Object.entries(c.clickParamMap).map(([k, v]) => `${k}=${v}`).join("\n")
          : "",
        clickOpenMode: c.clickOpenMode || "modal",
        clickModalTitle: c.clickModalTitle || "",
        wrap: c.wrap || false,
      }))
      : [];

    // Map filterConfigs → form filters (include all new UI fields)
    const filters = Array.isArray(record.filterConfigs)
      ? record.filterConfigs.map((f) => ({
        key: f.key || "",
        type: f.type || "exact",
        // Prefer the stored `column` field (available for all new records).
        // Fall back to regex-parsing sqlClause for legacy records that pre-date this fix.
        column: f.column || extractColumnFromClause(f.sqlClause),
        label: f.label || "",
        placeholder: f.placeholder || "",
        colSpan: f.colSpan || 3,
        required: f.required || false,
        hidden: f.hidden || false,
        options: Array.isArray(f.options) ? f.options.join(",") : (f.options || ""),
        dropdownApiUrl: f.dropdownApiUrl || "",
        defaultValue: f.defaultValue || "",
        labelField: f.labelField || "",
        valueField: f.valueField || "",
      }))
      : [];

    // Parse pageConfig
    const pc = record.pageConfig || {};
    const pageConfig = {
      defaultPageSize: pc.defaultPageSize || 20,
      pageSizeOptions: Array.isArray(pc.pageSizeOptions)
        ? pc.pageSizeOptions.join(",")
        : (pc.pageSizeOptions || "10,20,50,100"),
      downloadable: pc.downloadable ?? true,
      requiresFilter: pc.requiresFilter ?? false,
      filtersCollapsed: pc.filtersCollapsed ?? false,
      showSerialNumber: pc.showSerialNumber ?? true,
      showInList: pc.showInList ?? true,
      icon: pc.icon || "",
      dataUrl: pc.dataUrl || "",
      downloadUrl: pc.downloadUrl || "",
    };

    form.setFieldsValue({
      reportKey: record.reportKey,
      columns,
      displayName: record.displayName || "",
      datasource: record.datasource || "REPLICATION",
      batchSize: record.batchSize || 50000,
      sql: record.sql || "",
      downloadSql: record.downloadSql || "",
      isActive: record.isActive ?? true,
      filters,
      pageConfig,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditKey(null);
  };

  // ── Submit ──────────────────────────────────────────────────────────────

  const buildFilters = (rawFilters = []) =>
    (rawFilters || [])
      // Only drop rows that have no key at all (completely empty/blank rows)
      .filter((f) => f && f.key && f.key.trim())
      .map((f) => ({
        key: f.key,
        type: f.type || "exact",
        column: f.column || "",
        label: f.label || "",
        placeholder: f.placeholder || "",
        colSpan: f.colSpan || 3,
        required: f.required || false,
        hidden: f.hidden || false,
        options: f.options
          ? f.options.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        dropdownApiUrl: f.dropdownApiUrl || "",
        defaultValue: f.defaultValue || "",
        labelField: f.labelField || "",
        valueField: f.valueField || "",
      }));

  const buildColumns = (rawCols = []) =>
    (rawCols || [])
      .filter((c) => c && c.key)
      .map((c) => {
        // Parse clickParamMap from "key=value, key2=value2" textarea string
        let clickParamMap = {};
        if (c.clickParamMapRaw) {
          c.clickParamMapRaw.split("\n").forEach((line) => {
            const idx = line.indexOf("=");
            if (idx > 0) {
              const k = line.slice(0, idx).trim();
              const v = line.slice(idx + 1).trim();
              if (k) clickParamMap[k] = v;
            }
          });
        }
        return {
          key: c.key,
          header: c.header || "",
          cellType: c.cellType || "text",
          sortable: c.sortable ?? true,
          hidden: Boolean(c.hidden),
          width: c.width || "",
          badgeColors: c.badgeColorsRaw
            ? Object.fromEntries(
              c.badgeColorsRaw.split("\n").map((l) => {
                const i = l.indexOf("=");
                return i > 0 ? [l.slice(0, i).trim(), l.slice(i + 1).trim()] : null;
              }).filter(Boolean)
            )
            : undefined,
          linkTemplate: c.linkTemplate || "",
          linkTarget: c.linkTarget || "_blank",
          clickReportKey: c.clickReportKey || "",
          clickParamMap: Object.keys(clickParamMap).length ? clickParamMap : undefined,
          clickOpenMode: c.clickOpenMode || "modal",
          clickModalTitle: c.clickModalTitle || "",
          wrap: Boolean(c.wrap),
        };
      });

  const buildPageConfig = (pc = {}) => ({
    defaultPageSize: pc.defaultPageSize || 20,
    pageSizeOptions: pc.pageSizeOptions
      ? pc.pageSizeOptions.split(",").map((s) => parseInt(s.trim(), 10)).filter(Boolean)
      : [10, 20, 50, 100],
    downloadable: pc.downloadable ?? true,
    requiresFilter: pc.requiresFilter ?? false,
    filtersCollapsed: pc.filtersCollapsed ?? false,
    showSerialNumber: pc.showSerialNumber ?? true,
    showInList: pc.showInList ?? true,
    icon: pc.icon || "",
    dataUrl: pc.dataUrl?.trim() || "",
    downloadUrl: pc.downloadUrl?.trim() || "",
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const filters = buildFilters(values.filters);
      const columns = buildColumns(values.columns);
      const pageConfig = buildPageConfig(values.pageConfig);

      if (editKey) {
        const patchPayload = {
          displayName: (values.displayName || values.reportKey).trim(),
          datasource: values.datasource,
          sql: values.sql.trim(),
          downloadSql: values.downloadSql?.trim() || null,
          batchSize: values.batchSize || 50000,
          isActive: values.isActive ?? true,
          filters,
          // Always send columnUiConfig so the backend merge logic is triggered
          // and existing hidden/drilldown config is preserved on every edit.
          columnUiConfig: columns,
          pageConfig,
        };
        await ReportConfigService.patch(editKey, patchPayload);
      } else {
        const payload = {
          reportKey: values.reportKey.trim(),
          displayName: (values.displayName || values.reportKey).trim(),
          datasource: values.datasource,
          sql: values.sql.trim(),
          downloadSql: values.downloadSql?.trim() || null,
          batchSize: values.batchSize || 50000,
          isActive: values.isActive ?? true,
          filters,
          columns: columns.length ? columns : undefined,
          pageConfig,
        };
        await ReportConfigService.upsert(payload);
      }

      message.success(editKey ? "Config updated successfully!" : "Config created successfully!");
      closeModal();
      fetchData(pagination.current, pagination.pageSize);
    } catch (err) {
      if (err?.errorFields) return;
      message.error("Save failed: " + (err.message || "Unknown error"));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Toggle / Delete ─────────────────────────────────────────────────────

  const handleToggle = async (reportKey, currentActive) => {
    try {
      const res = await ReportConfigService.toggleActive(reportKey);
      message.success(res?.message || `${currentActive ? "Disabled" : "Enabled"} successfully`);
      fetchData(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error("Toggle failed: " + (err.message || "Unknown error"));
    }
  };

  const handleDelete = async (reportKey) => {
    try {
      await ReportConfigService.delete(reportKey);
      message.success(`Deleted "${reportKey}"`);
      fetchData(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error("Delete failed: " + (err.message || "Unknown error"));
    }
  };

  function extractColumnFromClause(clause = "") {
    if (!clause) return "";
    // Try: AND col_name >= / = / < / ANY ...  (simple column reference)
    let m = clause.match(/AND\s+([a-z_][a-z0-9_]*)\s*[=><!]/i);
    if (m) return m[1];
    // Try: AND LOWER(col_name) or AND TRIM(col_name) — extract col inside function
    m = clause.match(/AND\s+\w+\s*\(\s*([a-z_][a-z0-9_]*)\s*\)/i);
    if (m) return m[1];
    // Try: AND col_name = ANY(...)
    m = clause.match(/AND\s+([a-z_][a-z0-9_]*)\s*=\s*ANY/i);
    if (m) return m[1];
    // Last resort: first word-token after AND
    m = clause.match(/AND\s+(\w+)/i);
    return m ? m[1] : "";
  }

  // ── Table Columns ───────────────────────────────────────────────────────

  const columns = [
    {
      title: "#",
      width: 50,
      render: (_, __, idx) =>
        (pagination.current - 1) * pagination.pageSize + idx + 1,
    },
    {
      title: "Report Key",
      dataIndex: "reportKey",
      key: "reportKey",
      render: (val) => <span className="rcm-key-chip">{val}</span>,
    },
    {
      title: "Display Name",
      dataIndex: "displayName",
      key: "displayName",
      ellipsis: true,
      render: (val) => val || <Text type="secondary">—</Text>,
    },
    {
      title: "Datasource",
      dataIndex: "datasource",
      key: "datasource",
      width: 130,
      render: (val) =>
        val === "A3" ? (
          <Tag color="orange">A3</Tag>
        ) : (
          <Tag color="blue">REPLICATION</Tag>
        ),
    },
    {
      title: "Query Config",
      key: "sqlMode",
      width: 140,
      render: (_, record) =>
        record.downloadSql ? (
          <Tag color="purple">📦 Custom Download</Tag>
        ) : (
          <Tag color="default">Standard</Tag>
        ),
    },
    {
      title: "Filters",
      key: "filters",
      width: 80,
      render: (_, record) => {
        const count = Array.isArray(record.filterConfigs) ? record.filterConfigs.length : 0;
        return <Text style={{ color: "#4263eb" }}>{count} filter{count !== 1 ? "s" : ""}</Text>;
      },
    },
    {
      title: "UI Schema",
      key: "uiSchema",
      width: 90,
      render: (_, record) => {
        const hasSchema = record.uiSchemaUrl;
        return hasSchema ? (
          <Tag
            color="geekblue"
            style={{ cursor: "pointer" }}
            onClick={() => copyToClipboard(record.uiSchemaUrl)}
          >
            🧩 Schema
          </Tag>
        ) : (
          <Tag color="default">—</Tag>
        );
      },
    },
    {
      title: "API URLs",
      key: "urls",
      width: 200,
      render: (_, record) => {
        const dataUrl = record.dataUrl;
        const dlUrl = record.downloadUrl;
        const schemaUrl = record.uiSchemaUrl;

        if (!dataUrl && !dlUrl && !schemaUrl) return <Text type="secondary">—</Text>;

        return (
          <div className="rcm-url-btns">
            {dataUrl && (
              <Tooltip
                title={<span style={{ fontFamily: "monospace", fontSize: 12 }}>{dataUrl}</span>}
                overlayStyle={{ maxWidth: 480 }}
              >
                <span className="rcm-url-btn" onClick={() => copyToClipboard(dataUrl)}>
                  <CopyOutlined /> Data
                </span>
              </Tooltip>
            )}
            {dlUrl && (
              <Tooltip
                title={<span style={{ fontFamily: "monospace", fontSize: 12 }}>{dlUrl}</span>}
                overlayStyle={{ maxWidth: 480 }}
              >
                <span className="rcm-url-btn dl" onClick={() => copyToClipboard(dlUrl)}>
                  <DownloadOutlined /> CSV
                </span>
              </Tooltip>
            )}
            {schemaUrl && (
              <Tooltip
                title={<span style={{ fontFamily: "monospace", fontSize: 12 }}>{schemaUrl}</span>}
                overlayStyle={{ maxWidth: 480 }}
              >
                <span className="rcm-url-btn schema" onClick={() => copyToClipboard(schemaUrl)}>
                  <ApartmentOutlined /> UI
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: 90,
      render: (val) =>
        val ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Tag color="error">Inactive</Tag>
        ),
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 110,
      render: (val) =>
        val ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {String(val).substring(0, 10)}
          </Text>
        ) : (
          "—"
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title={`${record.isActive ? "Disable" : "Enable"} "${record.reportKey}"?`}
            onConfirm={() => handleToggle(record.reportKey, record.isActive)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              size="small"
              icon={<PoweroffOutlined />}
              type={record.isActive ? "default" : "primary"}
              danger={record.isActive}
            >
              {record.isActive ? "Disable" : "Enable"}
            </Button>
          </Popconfirm>
          <Popconfirm
            title={`Permanently delete "${record.reportKey}"?`}
            onConfirm={() => handleDelete(record.reportKey)}
            okText="Delete"
            cancelText="Cancel"
            okType="danger"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="rcm-container">
      {/* Heading */}
      <div style={{ backgroundColor: "#fff", padding: "8px 16px", borderRadius: 8, marginBottom: 16 }}>
        <Heading title="Report Config Manager" navigateTo="/reports">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openAdd}
            style={{ marginLeft: "auto" }}
          >
            Add New Report
          </Button>
        </Heading>
      </div>

      {/* Stats */}
      <div className="rcm-stats">
        {[
          { icon: "📋", label: "Total Reports", val: stats.total, cls: "blue" },
          { icon: "✅", label: "Active", val: stats.active, cls: "green" },
          { icon: "⛔", label: "Inactive", val: stats.inactive, cls: "red" },
          { icon: "🔀", label: "Split SQL", val: stats.split, cls: "orange" },
        ].map((s) => (
          <div className="rcm-stat-card" key={s.label}>
            <div className={`rcm-stat-icon ${s.cls}`}>{s.icon}</div>
            <div>
              <div className="rcm-stat-val">{s.val}</div>
              <div className="rcm-stat-lbl">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="reportKey"
          columns={columns}
          dataSource={tableData}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          size="middle"
        />
      </Card>

      {/* ── Add / Edit Modal ── */}
      <Modal
        title={
          <div className="rcm-modal-header">
            <div className="rcm-modal-icon">
              <ApiOutlined />
            </div>
            <div className="rcm-modal-titles">
              <div className="rcm-modal-main">{editKey ? `Edit Config: ${editKey}` : "Add New Report Config"}</div>
              <div className="rcm-modal-sub">{editKey ? "Update report query, filters & page settings" : "Set up a new dynamic data extraction endpoint"}</div>
            </div>
          </div>
        }
        open={modalOpen}
        onCancel={closeModal}
        width={920}
        className="rcm-premium-modal"
        footer={[
          <Button key="cancel" onClick={closeModal}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={submitting}
            onClick={handleSubmit}
            icon={<DownloadOutlined />}
          >
            Save Config
          </Button>,
        ]}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          initialValues={{
            datasource: "REPLICATION",
            batchSize: 50000,
            isActive: true,
            filters: [],
            pageConfig: {
              defaultPageSize: 20,
              pageSizeOptions: "10,20,50,100",
              downloadable: true,
              requiresFilter: false,
              filtersCollapsed: false,
              showSerialNumber: true,
            },
          }}
        >
          {/* Row 1: Key + Name */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="reportKey"
                label="Report Key"
                rules={[{ required: true, message: "Report Key is required" }]}
              >
                <Input
                  placeholder="e.g. drs-pendency-report"
                  disabled={!!editKey}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="displayName" label="Display Name">
                <Input placeholder="e.g. DRS Pendency Report" />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2: Datasource + Batch Size + Status */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="datasource"
                label="Datasource"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="REPLICATION">REPLICATION (default)</Option>
                  <Option value="A3">A3</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="batchSize" label="Batch Size (rows/CSV fetch)">
                <InputNumber min={1000} max={500000} step={10000} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="isActive" label="Status" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          {/* SQL Section */}
          <div className="rcm-sql-container">
            <div className="rcm-section-title">
              <ApiOutlined /> SQL Queries
            </div>
            <Tabs defaultActiveKey="data" type="card" size="small" className="rcm-premium-tabs">
              <TabPane tab={<span>📊 Data API SQL *</span>} key="data">
                <Form.Item
                  name="sql"
                  rules={[{ required: true, message: "Data API SQL is required" }]}
                  style={{ marginBottom: 0 }}
                >
                  <TextArea
                    rows={6}
                    placeholder={"SELECT col1, col2, hub_id\nFROM schema.table_name\nWHERE 1=1"}
                    className="rcm-textarea-dark"
                  />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.sql !== cur.sql}>
                  {({ getFieldValue }) => <SqlPreview sql={getFieldValue("sql")} />}
                </Form.Item>
              </TabPane>

              <TabPane tab={<span>⬇️ Download SQL (optional)</span>} key="download">
                <Form.Item name="downloadSql" style={{ marginBottom: 0 }}>
                  <TextArea
                    rows={6}
                    placeholder={
                      "SELECT col1, col2\nFROM schema.table_name\nWHERE 1=1\n\n(Leave empty to use Data API SQL for download)"
                    }
                    className="rcm-textarea-dark"
                  />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.downloadSql !== cur.downloadSql}>
                  {({ getFieldValue }) => <SqlPreview sql={getFieldValue("downloadSql")} />}
                </Form.Item>
              </TabPane>
            </Tabs>
          </div>

          {/* ── Filters Section ── */}
          <div className="rcm-section-block" style={{ marginTop: 16 }}>
            <div className="rcm-section-title">
              <FilterOutlined /> Filters <small style={{ color: "#8c8c8c", fontWeight: 400 }}>(optional)</small>
            </div>

            {/* Filter column headers */}
            <div className="rcm-filter-header-row">
              <span>Param Key *</span>
              <span>Type *</span>
              <span>DB Column *</span>
              <span>Label</span>
              <span>Placeholder</span>
              <span style={{ textAlign: "center" }}>Span</span>
              <span style={{ textAlign: "center" }}>Req?</span>
              <span>Options (comma-sep)</span>
              <span>Dropdown API URL</span>
              <span>Label Field</span>
              <span>Value Field</span>
              <span></span>
            </div>

            <Form.List name="filters">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div className="rcm-filter-row-expanded" key={key}>
                      {/* Param Key */}
                      <Form.Item {...restField} name={[name, "key"]} style={{ margin: 0 }}>
                        <Input placeholder="fromDate" size="small" />
                      </Form.Item>
                      {/* Type */}
                      <Form.Item {...restField} name={[name, "type"]} style={{ margin: 0 }}>
                        <Select size="small" placeholder="type">
                          {FILTER_TYPES.map((t) => (
                            <Option key={t.value} value={t.value}>{t.value}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                      {/* DB Column */}
                      <Form.Item {...restField} name={[name, "column"]} style={{ margin: 0 }}>
                        <Input placeholder="hub_id" size="small" />
                      </Form.Item>
                      {/* Label */}
                      <Form.Item {...restField} name={[name, "label"]} style={{ margin: 0 }}>
                        <Input placeholder="From Date" size="small" />
                      </Form.Item>
                      {/* Placeholder */}
                      <Form.Item {...restField} name={[name, "placeholder"]} style={{ margin: 0 }}>
                        <Input placeholder="Select date..." size="small" />
                      </Form.Item>
                      {/* ColSpan */}
                      <Form.Item {...restField} name={[name, "colSpan"]} style={{ margin: 0 }}>
                        <InputNumber min={1} max={12} size="small" style={{ width: "100%" }} placeholder="3" />
                      </Form.Item>
                      {/* Required */}
                      <Form.Item {...restField} name={[name, "required"]} valuePropName="checked" style={{ margin: 0, textAlign: "center" }}>
                        <Switch size="small" />
                      </Form.Item>
                      {/* Options */}
                      <Form.Item {...restField} name={[name, "options"]} style={{ margin: 0 }}>
                        <Input placeholder="A,B,C" size="small" />
                      </Form.Item>
                      {/* Dropdown API URL */}
                      <Form.Item {...restField} name={[name, "dropdownApiUrl"]} style={{ margin: 0 }}>
                        <Input placeholder="/api/v1/hubs" size="small" />
                      </Form.Item>
                      {/* Label Field (for dynamic_select: field to show as text) */}
                      <Form.Item {...restField} name={[name, "labelField"]} style={{ margin: 0 }}>
                        <Input placeholder="hub_name" size="small" />
                      </Form.Item>
                      {/* Value Field (for dynamic_select: field to send as value) */}
                      <Form.Item {...restField} name={[name, "valueField"]} style={{ margin: 0 }}>
                        <Input placeholder="hub_id" size="small" />
                      </Form.Item>
                      {/* Remove */}
                      <MinusCircleOutlined
                        style={{ color: "#ff4d4f", cursor: "pointer", fontSize: 16 }}
                        onClick={() => remove(name)}
                      />
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() =>
                      add({
                        key: "",
                        type: "exact",
                        column: "",
                        label: "",
                        placeholder: "",
                        colSpan: 3,
                        required: false,
                        hidden: false,
                        options: "",
                        dropdownApiUrl: "",
                        defaultValue: "",
                      })
                    }
                    block
                    icon={<PlusOutlined />}
                    style={{ marginTop: 8 }}
                  >
                    Add Filter
                  </Button>
                </>
              )}
            </Form.List>
          </div>

          {/* ── Page Config Section ── */}
          <Divider style={{ margin: "20px 0 0" }} />
          <Collapse
            ghost
            defaultActiveKey={[]}
            style={{ marginBottom: 0 }}
          >
            <Panel
              header={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <SettingOutlined style={{ color: "#4263eb" }} />
                  <span className="rcm-section-title" style={{ margin: 0 }}>
                    Page Config <small style={{ color: "#8c8c8c", fontWeight: 400 }}>(UI settings — expand to edit)</small>
                  </span>
                </div>
              }
              key="page"
            >
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={8}>
                  <Form.Item name={["pageConfig", "defaultPageSize"]} label="Default Page Size">
                    <InputNumber min={5} max={500} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={["pageConfig", "pageSizeOptions"]} label="Page Size Options (comma)">
                    <Input placeholder="10,20,50,100" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={["pageConfig", "icon"]} label="Page Icon (emoji/text)">
                    <Input placeholder="📦" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={6}>
                  <Form.Item name={["pageConfig", "downloadable"]} label="Downloadable" valuePropName="checked">
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={["pageConfig", "requiresFilter"]} label="Requires Filter" valuePropName="checked">
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={["pageConfig", "filtersCollapsed"]} label="Filters Collapsed" valuePropName="checked">
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={["pageConfig", "showSerialNumber"]} label="Show S.No" valuePropName="checked">
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={["pageConfig", "showInList"]} label="Show in Listing" valuePropName="checked">
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                  </Form.Item>
                </Col>
              </Row>
              <Divider style={{ margin: "4px 0 12px" }} />
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={["pageConfig", "dataUrl"]}
                    label={
                      <span>
                        📡 Data API URL
                        <small style={{ color: "#8c8c8c", fontWeight: 400, marginLeft: 6 }}>
                          (leave blank to use generic SQL engine)
                        </small>
                      </span>
                    }
                  >
                    <Input
                      placeholder="https://api.example.com/v1/report/booking-status"
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={["pageConfig", "downloadUrl"]}
                    label={
                      <span>
                        ⬇️ Download API URL
                        <small style={{ color: "#8c8c8c", fontWeight: 400, marginLeft: 6 }}>
                          (leave blank to use generic SQL engine)
                        </small>
                      </span>
                    }
                  >
                    <Input
                      placeholder="https://api.example.com/v1/report/booking-status/download"
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          </Collapse>

          {/* ── Column Config Section ── */}
          <Divider style={{ margin: "8px 0 0" }} />
          <Collapse ghost defaultActiveKey={[]} style={{ marginBottom: 0 }}>
            <Panel
              header={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <TableOutlined style={{ color: "#7c3aed" }} />
                  <span className="rcm-section-title" style={{ margin: 0 }}>
                    Column Config <small style={{ color: "#8c8c8c", fontWeight: 400 }}>(override column display, badges, clickable drill-downs — expand to edit)</small>
                  </span>
                </div>
              }
              key="cols"
            >
              <div style={{ background: "#fafafa", borderRadius: 8, padding: "8px 0 4px" }}>
                {/* column list header */}
                <div className="rcm-col-header-row">
                  <span>Column Key *</span>
                  <span>Header Label</span>
                  <span>Cell Type</span>
                  <span style={{ textAlign: "center" }}>Sort?</span>
                  <span style={{ textAlign: "center" }}>Hide?</span>
                  <span style={{ textAlign: "center" }}>Wrap?</span>
                  <span>Width</span>
                  <span>Badge Colors (KEY=color, one/line)</span>
                  <span>Link Template</span>
                  <span></span>
                </div>

                <Form.List name="columns">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <div key={key}>
                          {/* ── Basic row ── */}
                          <div className="rcm-col-row">
                            <Form.Item {...restField} name={[name, "key"]} style={{ margin: 0 }}>
                              <Input placeholder="pending_count" size="small" />
                            </Form.Item>
                            <Form.Item {...restField} name={[name, "header"]} style={{ margin: 0 }}>
                              <Input placeholder="Pending" size="small" />
                            </Form.Item>
                            <Form.Item {...restField} name={[name, "cellType"]} style={{ margin: 0 }}>
                              <Select size="small" placeholder="text">
                                <Option value="text">text</Option>
                                <Option value="number">number</Option>
                                <Option value="date">date</Option>
                                <Option value="badge">badge</Option>
                                <Option value="link">link</Option>
                                <Option value="clickable_count">clickable_count 🖱️</Option>
                                <Option value="clickable_text">clickable_text 🖱️</Option>
                              </Select>
                            </Form.Item>
                            <Form.Item {...restField} name={[name, "sortable"]} valuePropName="checked" style={{ margin: 0, textAlign: "center" }}>
                              <Switch size="small" defaultChecked />
                            </Form.Item>
                            <Form.Item {...restField} name={[name, "hidden"]} valuePropName="checked" style={{ margin: 0, textAlign: "center" }}>
                              <Switch size="small" />
                            </Form.Item>
                            <Form.Item {...restField} name={[name, "wrap"]} valuePropName="checked" style={{ margin: 0, textAlign: "center" }}>
                              <Switch size="small" />
                            </Form.Item>
                            <Form.Item {...restField} name={[name, "width"]} style={{ margin: 0 }}>
                              <Input placeholder="120px" size="small" />
                            </Form.Item>
                            <Form.Item {...restField} name={[name, "badgeColorsRaw"]} style={{ margin: 0 }}>
                              <TextArea
                                rows={2}
                                placeholder={"DELIVERED=#22c55e\nPENDING=#f59e0b\nFAILED=#ef4444"}
                                style={{ fontSize: 11, resize: "none" }}
                              />
                            </Form.Item>
                            <Form.Item {...restField} name={[name, "linkTemplate"]} style={{ margin: 0 }}>
                              <Input placeholder="/shipment/{awb_no}" size="small" />
                            </Form.Item>
                            <MinusCircleOutlined
                              style={{ color: "#ff4d4f", cursor: "pointer", fontSize: 15 }}
                              onClick={() => remove(name)}
                            />
                          </div>

                          {/* ── Drill-down sub-row (only shown when cellType is clickable) ── */}
                          <Form.Item
                            noStyle
                            shouldUpdate={(prev, cur) =>
                              prev.columns?.[name]?.cellType !== cur.columns?.[name]?.cellType
                            }
                          >
                            {({ getFieldValue }) => {
                              const ct = getFieldValue(["columns", name, "cellType"]);
                              if (ct !== "clickable_count" && ct !== "clickable_text") return null;
                              return (
                                <div className="rcm-col-drilldown-row">
                                  <div style={{ fontSize: 11, color: "#7c3aed", fontWeight: 600, marginBottom: 6 }}>
                                    🖱️ Drill-Down Config
                                  </div>
                                  <Row gutter={12}>
                                    <Col span={6}>
                                      <Form.Item {...restField} name={[name, "clickReportKey"]} label={<small>Target Report Key</small>} style={{ marginBottom: 8 }}>
                                        <Input placeholder="shipment-pendency-detail" size="small" />
                                      </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                      <Form.Item {...restField} name={[name, "clickOpenMode"]} label={<small>Open Mode</small>} style={{ marginBottom: 8 }}>
                                        <Select size="small" defaultValue="modal">
                                          <Option value="modal">modal</Option>
                                          <Option value="drawer">drawer</Option>
                                          <Option value="page">page</Option>
                                        </Select>
                                      </Form.Item>
                                    </Col>
                                    <Col span={6}>
                                      <Form.Item {...restField} name={[name, "clickModalTitle"]} label={<small>{"Modal Title (use {row.col})"}</small>} style={{ marginBottom: 8 }}>
                                        <Input placeholder="Pending at {row.hub_name}" size="small" />
                                      </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                      <Form.Item {...restField} name={[name, "clickParamMapRaw"]} label={<small>Param Map (key=value, one per line)</small>} style={{ marginBottom: 8 }}>
                                        <TextArea
                                          rows={3}
                                          placeholder={"hubId={row.hub_id}\nstatus=PENDING\nfromDate=__filter.fromDate"}
                                          style={{ fontSize: 11 }}
                                        />
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </div>
                              );
                            }}
                          </Form.Item>
                        </div>
                      ))}
                      <Button
                        type="dashed"
                        onClick={() => add({ key: "", header: "", cellType: "text", sortable: true, hidden: false })}
                        block
                        icon={<PlusOutlined />}
                        style={{ marginTop: 8 }}
                      >
                        Add Column Override
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </Panel>
          </Collapse>
        </Form>
      </Modal>
    </div>
  );
};

export default ReportConfigManager;
